import { Modal, Form, Input, message } from "antd";
import React, { useState } from "react";
import { changePassword } from "../../services/auth";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error("Las nuevas contraseñas no coinciden");
        return;
      }
      setLoading(true);
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success("Contraseña actualizada correctamente");
      form.resetFields();
      onClose();
    } catch (error: any) {
      if (error?.response?.data?.includes("invalid current password")) {
        message.error("La contraseña actual es incorrecta");
      } else if (error?.errorFields) {
        // Validation errors from Ant Design
        return;
      } else {
        message.error("Error al actualizar la contraseña");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Cambiar Contraseña"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Guardar"
      cancelText="Cancelar"
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          name="currentPassword"
          label="Contraseña Actual"
          rules={[
            { required: true, message: "Ingresa tu contraseña actual" },
          ]}
        >
          <Input.Password
            placeholder="Contraseña actual"
            style={{ borderRadius: "8px" }}
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="Nueva Contraseña"
          tooltip="Mínimo 8 caracteres, 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial."
          rules={[
            { required: true, message: "Ingresa la nueva contraseña" },
            { min: 8, message: "Mínimo 8 caracteres" },
            { pattern: /[A-Z]/, message: "Debe incluir una letra mayúscula" },
            { pattern: /[a-z]/, message: "Debe incluir una letra minúscula" },
            { pattern: /[0-9]/, message: "Debe incluir un número" },
            { pattern: /[^A-Za-z0-9]/, message: "Debe incluir un carácter especial" }
          ]}
        >
          <Input.Password
            placeholder="Nueva contraseña"
            style={{ borderRadius: "8px" }}
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirmar Nueva Contraseña"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Confirma la nueva contraseña" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Las contraseñas no coinciden")
                );
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Confirmar nueva contraseña"
            style={{ borderRadius: "8px" }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
